import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../utils/api';
import { parentSubscriptions, schoolSubscriptions, subscriptionFAQ } from '../../config/subscription';
import { FaCheck, FaTimes, FaInfoCircle } from 'react-icons/fa';

const Subscriptions = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  const [userType, setUserType] = useState('parent');
  const [billingCycle, setBillingCycle] = useState('annual');
  const [selectedTier, setSelectedTier] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const [userSubscription, setUserSubscription] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  useEffect(() => {
    // If user is logged in, fetch their current subscription
    if (isAuthenticated) {
      fetchUserSubscription();
    }
    
    // Set default user type based on user role
    if (currentUser) {
      if (['system_admin', 'school_admin'].includes(currentUser.role)) {
        setUserType('school');
      } else {
        setUserType('parent');
      }
    }
  }, [currentUser, isAuthenticated]);

  const fetchUserSubscription = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/subscriptions/me');
      setUserSubscription(response.data.data);
    } catch (error) {
      // If no subscription found, that's okay
      console.log('No active subscription found');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = (tier) => {
    if (!isAuthenticated) {
      showToast('Please login to subscribe to a plan', 'error');
      navigate('/login');
      return;
    }
    
    setSelectedTier(tier);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = () => {
    navigate(`/checkout?tier=${selectedTier}&billing=${billingCycle}`, { 
      state: { 
        selectedPlan: selectedTier,
        billingCycle,
        userType 
      } 
    });
  };

  const toggleFAQ = (index) => {
    if (expandedFAQ === index) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(index);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-primary-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Choose the right plan for you
          </h1>
          <p className="mt-6 text-xl max-w-2xl mx-auto">
            Get access to mentorship, school management tools, and educational resources with our flexible subscription plans.
          </p>
          
          {/* User type toggle */}
          <div className="mt-10 bg-white p-1 rounded-full inline-flex">
            <button
              className={`py-2 px-6 rounded-full text-sm font-medium ${
                userType === 'parent' ? 'bg-primary-500 text-white' : 'text-gray-700'
              }`}
              onClick={() => setUserType('parent')}
            >
              For Parents
            </button>
            <button
              className={`py-2 px-6 rounded-full text-sm font-medium ${
                userType === 'school' ? 'bg-primary-500 text-white' : 'text-gray-700'
              }`}
              onClick={() => setUserType('school')}
            >
              For Schools
            </button>
          </div>
          
          {/* Billing cycle toggle (only for parents) */}
          {userType === 'parent' && (
            <div className="mt-6 bg-white p-1 rounded-full inline-flex">
              <button
                className={`py-2 px-6 rounded-full text-sm font-medium ${
                  billingCycle === 'quarterly' ? 'bg-primary-500 text-white' : 'text-gray-700'
                }`}
                onClick={() => setBillingCycle('quarterly')}
              >
                Quarterly
              </button>
              <button
                className={`py-2 px-6 rounded-full text-sm font-medium ${
                  billingCycle === 'annual' ? 'bg-primary-500 text-white' : 'text-gray-700'
                }`}
                onClick={() => setBillingCycle('annual')}
              >
                Annual (Save 15%)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'plans'
                ? 'border-b-2 border-primary-500 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('plans')}
          >
            Subscription Plans
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'faq'
                ? 'border-b-2 border-primary-500 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('faq')}
          >
            FAQ
          </button>
        </div>

        {/* Checkout section if plan is selected */}
        {selectedTier && activeTab === 'plans' && (
          <div className="bg-white shadow-md rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between flex-wrap">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {userType === 'parent'
                    ? parentSubscriptions[selectedTier].name
                    : schoolSubscriptions[selectedTier].name}{' '}
                  Plan
                </h2>
                <p className="text-gray-500 mt-1">
                  {userType === 'parent' && billingCycle === 'annual'
                    ? parentSubscriptions[selectedTier].price.annual
                    : userType === 'parent'
                    ? parentSubscriptions[selectedTier].price.quarterly
                    : schoolSubscriptions[selectedTier].price.annual}
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={handleSubscribe}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Proceed to Checkout
                </button>
                <button
                  onClick={() => setSelectedTier(null)}
                  className="ml-4 inline-flex items-center px-4 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        {activeTab === 'plans' && (
          <div className="mt-8 grid gap-8 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
            {Object.keys(
              userType === 'parent' ? parentSubscriptions : schoolSubscriptions
            ).map((tier) => {
              const plan =
                userType === 'parent'
                  ? parentSubscriptions[tier]
                  : schoolSubscriptions[tier];
              
              // Skip free tier in display
              if (tier === 'free' && userType === 'parent') return null;
              
              return (
                <div
                  key={tier}
                  className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                    plan.highlighted ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  {plan.highlighted && (
                    <div className="bg-primary-500 text-white text-center py-2 font-medium">
                      {plan.title}
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
                    <p className="mt-1 text-gray-500">{plan.description}</p>
                    <p className="mt-4 text-3xl font-extrabold text-gray-900">
                      {userType === 'parent' && billingCycle === 'annual'
                        ? plan.price.annual
                        : userType === 'parent'
                        ? plan.price.quarterly
                        : plan.price.annual}
                    </p>
                    {userType === 'parent' && plan.perMonth && (
                      <p className="text-sm text-gray-500">
                        {billingCycle === 'annual'
                          ? plan.perMonth.annual
                          : plan.perMonth.quarterly}
                      </p>
                    )}
                    <ul className="mt-6 space-y-4">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="flex-shrink-0">
                            <FaCheck className="h-5 w-5 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">{feature}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      <button
                        onClick={() => handleSelectPlan(tier)}
                        disabled={plan.disabled}
                        className={`w-full px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
                          plan.disabled
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                        }`}
                      >
                        {plan.buttonText}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        {activeTab === 'faq' && (
          <div className="mt-8 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
            {subscriptionFAQ.map((faq, index) => (
              <div key={index} className="bg-white shadow overflow-hidden sm:rounded-md">
                <button
                  className="w-full px-4 py-5 sm:px-6 text-left flex justify-between items-center"
                  onClick={() => toggleFAQ(index)}
                >
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {faq.question}
                  </h3>
                  <span className="ml-4 flex-shrink-0">
                    {expandedFAQ === index ? (
                      <FaTimes className="h-5 w-5 text-gray-400" />
                    ) : (
                      <FaInfoCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <p className="text-base text-gray-500">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
